import { Alert, Button, Modal, ModalBody, ModalFooter } from '@execonline-inc/execonline-ui';
import { assertNever } from '@kofno/piper';
import { just } from 'maybeasy';
import { observer } from 'mobx-react';
import * as React from 'react';
import { T, TPlainTextKey } from '../../../../../Translations';
import Loader from '../../../../Loader';
import { shareStore } from '../Stores/ShareStore';
import CollectionSummary from './CollectionSummary';
import Footer from './Footer';
import Header from './Header';
import { treatmentOf } from './HiddenCourseTreatment';
import LicenseGroups from './LicenseGroups';
import { licenseAlertsStore } from './LicenseAlertsStore';
import { LicenseAlertsState } from './LicenseAlertsStore/Types';
import { unrecognizedEmails } from './ParticipantLists';
import {
  enrollableCourseCount,
  invitationCount,
  peopleWithoutFullAccess,
  severityOf,
  soleParticipant,
  totalCourses,
  totalParticipants,
} from './Selectors';

const isRefreshing = (state: LicenseAlertsState): boolean => {
  switch (state.kind) {
    case 'loading':
      return true;
    case 'waiting':
    case 'ready':
    case 'error':
      return false;
    default:
      return assertNever(state);
  }
};

const content = (state: LicenseAlertsState): React.ReactNode => {
  switch (state.kind) {
    case 'waiting':
    case 'loading':
      return (
        <ModalBody className="items-center">
          <Loader
            type="spin"
            text={just<TPlainTextKey>('Loading...')}
            className="py-8"
            height={40}
            width={40}
            delay={0}
          />
        </ModalBody>
      );
    case 'error':
      return (
        <>
          <ModalBody>
            <Alert status="error" variant="outlined" icon radius="md">
              <T kind={state.message} />
            </Alert>
          </ModalBody>
          <ModalFooter className="flex-row justify-end">
            <Button
              variant="outlined"
              onClick={shareStore.cancelReview}
              data-intercom-target="share-license-alert-error-close"
            >
              <T kind="Close" />
            </Button>
          </ModalFooter>
        </>
      );
    case 'ready': {
      const alerts = state.licenseAlertsResource.payload;
      const unrecognized = unrecognizedEmails(alerts, shareStore.participants).length;
      const severity = severityOf(alerts, unrecognized);
      const sole = soleParticipant(alerts, unrecognized).map((participant) => ({
        firstName: participant.firstName,
        enrollable: alerts.licenseGroups
          .filter((group) => group.participants.some((p) => p.id === participant.id))
          .reduce((_, group) => enrollableCourseCount(alerts, group), 0),
      }));

      return (
        <>
          <Header
            severity={severity}
            withGaps={peopleWithoutFullAccess(alerts, unrecognized)}
            total={totalParticipants(alerts) + unrecognized}
            sole={sole}
            totalCourses={totalCourses(alerts)}
          />
          <ModalBody
            className="gap-5 min-h-0 overflow-y-auto"
            data-intercom-target="share-license-alerts-body"
          >
            <CollectionSummary programFamilies={alerts.collection.programFamilies} />
            <LicenseGroups alerts={alerts} access={treatmentOf(shareStore.state)} />
          </ModalBody>
          <Footer
            refreshing={isRefreshing(state)}
            invitationCount={invitationCount(alerts)}
            canSend={shareStore.hasInvitableParticipants}
            willSendEmail={shareStore.willSendEmail}
            reviewEntry={shareStore.reviewEntry}
            onCancel={shareStore.cancelReview}
            onRefresh={licenseAlertsStore.refresh}
            onSend={shareStore.submitInvitation}
          />
        </>
      );
    }
    default:
      return assertNever(state);
  }
};

// One Modal hosts every state so loading swaps content in place rather than
// flashing a second dialog. Capped at the viewport so a long course list scrolls
// inside the body while the header and the Send footer stay on screen
// (DVLP-7982: one chip per license made the list several times taller).
function LicenseAlertsModal() {
  return (
    <Modal
      open
      size="3xl"
      className="max-h-[calc(100vh-4rem)]"
      onClose={shareStore.cancelReview}
      data-intercom-target="share-license-alerts-modal"
    >
      {content(licenseAlertsStore.state)}
    </Modal>
  );
}

export default observer(LicenseAlertsModal);
